import { z } from "zod";
import { Id } from "../common/ids.js";
import { LocalizedText } from "../common/localized-text.js";
import { Timestamp } from "../common/time.js";
import { defineRoute } from "../http/route.js";
const Tenant = { surface: "tenant", auth: "user" };
/** Ten characters of Crockford base32, shown as `XXXXX-XXXXX`; case and the hyphen are ignored on input. */
export const CardCode = z
    .string()
    .regex(/^[0-9A-Za-z]{5}-?[0-9A-Za-z]{5}$/, "A card code has ten letters and digits")
    .meta({ id: "CardCode" });
/**
 * A business card: what an accepted external contact sees of a person, snapshotted when the contact is made and
 * refreshed when the person's department, position, or membership changes (DIR-2).
 */
export const BusinessCard = z
    .object({
    userId: Id,
    displayName: z.string(),
    avatarFileId: Id.nullable(),
    email: z.string().nullable(),
    enterprise: z.object({ id: Id, displayName: LocalizedText, verified: z.boolean() }),
    department: LocalizedText.nullable(),
    position: LocalizedText.nullable(),
})
    .meta({ id: "BusinessCard" });
export const MyCard = z
    .object({ card: BusinessCard, shareCode: z.string(), rotatedAt: Timestamp })
    .meta({ id: "MyCard" });
export const ContactRequestStatus = z
    .enum(["pending", "accepted", "declined", "expired"])
    .meta({ id: "ContactRequestStatus" });
export const ContactRequest = z
    .object({
    id: Id,
    direction: z.enum(["incoming", "outgoing"]),
    channel: z.enum(["card_code", "email", "phone"]),
    target: z
        .string()
        .nullable()
        .describe("Outgoing only: the card code, email, or phone the request was sent to"),
    from: BusinessCard.nullable().describe("Incoming only: the requester's card"),
    message: z.string().nullable(),
    status: ContactRequestStatus,
    createdAt: Timestamp,
    expiresAt: Timestamp,
    answeredAt: Timestamp.nullable(),
})
    .meta({ id: "ContactRequest" });
export const Contact = z
    .object({
    id: Id,
    card: BusinessCard,
    status: z.enum(["active", "left"]).describe("`left` when the person has left the enterprise on the card"),
    since: Timestamp,
    updatedAt: Timestamp,
})
    .meta({ id: "Contact" });
const Page = {
    limit: z.coerce.number().int().min(1).max(200).default(100),
    cursor: z.string().regex(/^\d+$/).optional().describe("Opaque; pass nextCursor back"),
};
const RequestId = z.object({ requestId: Id });
export const getMyCard = defineRoute({
    ...Tenant,
    id: "directory.card.get",
    method: "GET",
    path: "/v1/me/card",
    permission: "directory:read",
    marks: ["tenant"],
    summary: "The caller's business card in the active enterprise and the code others use to request contact",
    prd: ["DIR-2"],
    responses: { 200: { description: "The card", schema: MyCard } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED"],
});
export const rotateMyCard = defineRoute({
    ...Tenant,
    id: "directory.card.rotate",
    method: "POST",
    path: "/v1/me/card/rotate",
    permission: "directory:read",
    marks: ["tenant", "idem"],
    summary: "Replace the share code; the old code stops working at once",
    prd: ["DIR-2"],
    responses: { 200: { description: "The card with its new code", schema: MyCard } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED"],
});
export const createContactRequest = defineRoute({
    ...Tenant,
    id: "directory.contact_requests.create",
    method: "POST",
    path: "/v1/contact-requests",
    permission: "contact:manage",
    marks: ["tenant", "verified", "idem"],
    summary: "Ask a person in another enterprise to become an external contact, by card code or exact email or phone; the answer is the same whether or not the person exists",
    prd: ["DIR-2", "DIR-4"],
    request: {
        body: z
            .object({
            cardCode: CardCode.optional(),
            email: z.email().max(254).optional(),
            phone: z
                .string()
                .regex(/^\+[1-9]\d{6,14}$/, "E.164 phone number")
                .optional(),
            message: z.string().trim().min(1).max(280).optional(),
        })
            .refine((value) => [value.cardCode, value.email, value.phone].filter((field) => field !== undefined).length === 1, { message: "Give one of a card code, an email, or a phone number", path: ["cardCode"] }),
    },
    responses: { 202: { description: "The request, pending", schema: ContactRequest } },
    errors: [
        "AUTH_REQUIRED",
        "TENANT_REQUIRED",
        "PERMISSION_DENIED",
        "VALIDATION_FAILED",
        "ENTERPRISE_NOT_VERIFIED",
        "ENTERPRISE_SUSPENDED",
        "AGREEMENT_ACCEPTANCE_REQUIRED",
        "RATE_LIMITED",
    ],
});
export const listContactRequests = defineRoute({
    ...Tenant,
    id: "directory.contact_requests.list",
    method: "GET",
    path: "/v1/contact-requests",
    permission: "contact:manage",
    marks: ["tenant"],
    summary: "Pending requests to the caller, or every request the caller sent in the active enterprise, newest first",
    prd: ["DIR-2"],
    request: {
        query: z.object({ direction: z.enum(["incoming", "outgoing"]).default("incoming"), ...Page }),
    },
    responses: {
        200: {
            description: "Requests",
            schema: z.object({ items: z.array(ContactRequest), nextCursor: z.string().nullable() }),
        },
    },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED"],
});
export const acceptContactRequest = defineRoute({
    ...Tenant,
    id: "directory.contact_requests.accept",
    method: "POST",
    path: "/v1/contact-requests/{requestId}/accept",
    permission: "contact:manage",
    marks: ["tenant", "verified", "idem"],
    summary: "Accept a request: both people become each other's external contacts with a card snapshot",
    prd: ["DIR-2"],
    request: { params: RequestId },
    responses: { 200: { description: "The new contact", schema: Contact } },
    errors: [
        "AUTH_REQUIRED",
        "TENANT_REQUIRED",
        "PERMISSION_DENIED",
        "ENTERPRISE_NOT_VERIFIED",
        "ENTERPRISE_SUSPENDED",
        "AGREEMENT_ACCEPTANCE_REQUIRED",
        "NOT_FOUND",
        "INVALID_TRANSITION",
    ],
});
export const declineContactRequest = defineRoute({
    ...Tenant,
    id: "directory.contact_requests.decline",
    method: "POST",
    path: "/v1/contact-requests/{requestId}/decline",
    permission: "contact:manage",
    marks: ["tenant", "idem"],
    summary: "Decline a request",
    prd: ["DIR-2"],
    request: { params: RequestId },
    responses: { 204: { description: "Declined" } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND", "INVALID_TRANSITION"],
});
export const listContacts = defineRoute({
    ...Tenant,
    id: "directory.contacts.list",
    method: "GET",
    path: "/v1/contacts",
    permission: "contact:manage",
    marks: ["tenant"],
    summary: "The caller's external contacts in the active enterprise, by name, flagged when a person has left",
    prd: ["DIR-2", "DIR-4"],
    request: { query: z.object({ q: z.string().trim().min(1).max(100).optional(), ...Page }) },
    responses: {
        200: {
            description: "Contacts",
            schema: z.object({ items: z.array(Contact), nextCursor: z.string().nullable() }),
        },
    },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED"],
});
export const removeContact = defineRoute({
    ...Tenant,
    id: "directory.contacts.remove",
    method: "DELETE",
    path: "/v1/contacts/{contactId}",
    permission: "contact:manage",
    marks: ["tenant"],
    summary: "Remove an external contact from the caller's side only",
    prd: ["DIR-2"],
    request: { params: z.object({ contactId: Id }) },
    responses: { 204: { description: "Removed" } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND"],
});
export const contactRoutes = [
    getMyCard,
    rotateMyCard,
    createContactRequest,
    listContactRequests,
    acceptContactRequest,
    declineContactRequest,
    listContacts,
    removeContact,
];
//# sourceMappingURL=contacts.routes.js.map